import { textGray600 } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { IconButton } from '@osmosis/tailwind-ui/components/IconButton';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { H2 } from '@osmosis/tailwind-ui/components/Text';

import React, { MouseEvent, useState, useEffect } from 'react';
import { faEdit } from '@fortawesome/pro-solid-svg-icons/faEdit';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { PlaylistProps, PlaylistType } from '@osmosis/shared/types/playlist';

import InputEdit from '../../../components/InputEdit';
import { FormattedMessage, useIntl } from 'react-intl';
import { Tooltip } from '@osmosis/tailwind-ui/components/tooltip/Tooltip';
import { usePlaylistContext } from '../../../context/PlaylistProvider';
import InputLabel from '../../../components/auth/InputLabel';

type Props = {
  playlist: PlaylistProps;
  isPlaylistMine: boolean;
};

/**
 * An editable playlist title.
 */
const EditablePlaylistTitle = (props: Props) => {
  const { playlist, isPlaylistMine } = props;
  const intl = useIntl();

  const titleState = useState(playlist?.title);
  const [title, setTitle] = titleState;
  const [isPlaylistEditTitleVisible, setPlaylistEditTitleVisible] =
    useState(false);

  const { renamePlaylist } = usePlaylistContext();

  const togglePlaylistEditTitle = (e?: MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    if (!isPlaylistEditTitleVisible) setTitle(playlist?.title);
    setPlaylistEditTitleVisible((prev) => !prev);
  };

  const handleRenamePlaylist = async (name: string) => {
    await renamePlaylist(name, playlist?.url);
    playlist.title = name;
  };

  const handleSave = async () => {
    await handleRenamePlaylist(title);
    setPlaylistEditTitleVisible(false);
  };

  useEffect(() => {
    if (isPlaylistEditTitleVisible) togglePlaylistEditTitle();
  }, [playlist.url]);

  return (
    <>
      {isPlaylistEditTitleVisible ? (
        <Flex className={`flex-col`}>
          <InputLabel
            content={
              <FormattedMessage
                id="edit-playlist-title"
                defaultMessage="Title"
              />
            }
            htmlFor="input-edit"
            data-testid="edit-playlist-title"
          />

          <InputEdit
            htmlFor="input-edit"
            inputState={titleState}
            onConfirm={handleSave}
            onCancel={togglePlaylistEditTitle}
          />
        </Flex>
      ) : (
        <Flex className={`items-center`}>
          <H2 data-testid="playlist-name" className={`mr-2`}>
            {playlist?.title}
          </H2>
          {playlist.type !== PlaylistType.WATCH_LATER && playlist.userOwned && (
            <Box className={`opacity-50`}>
              {isPlaylistMine && (
                <IconButton
                  type="button"
                  variant="transparent"
                  icon={<Icon icon={faEdit} />}
                  onClick={togglePlaylistEditTitle}
                  data-testid="change-playlistname-btn"
                  data-tip
                  data-for="edit-playlist-name"
                  aria-label={intl.formatMessage({
                    id: 'change-playlistname-btn',
                    defaultMessage: 'Edit Playlist Name',
                  })}
                  className={`${textGray600} text-[18px]`}
                />
              )}

              {!isPlaylistEditTitleVisible && (
                <Tooltip
                  id="edit-playlist-name"
                  className="bg-black text-white"
                >
                  <P className={`text-[13px] text-inherit`}>
                    <FormattedMessage
                      id="learn.playlist.edit_name"
                      defaultMessage="Edit name"
                    />
                  </P>
                </Tooltip>
              )}
            </Box>
          )}
        </Flex>
      )}
    </>
  );
};

export default EditablePlaylistTitle;
